import React, { useEffect, useRef, useState } from 'react';
import { Share2 } from 'lucide-react';
import { userAPI } from '@/api/userAPI';
import { shareAPI } from '@/api/shareAPI';
import toast from 'react-hot-toast';
import { projectMemberAPI } from '@/api/projectMemberAPI';

interface ShareProps {
  label?: string;
  iconOnly?: boolean;
  subject?: string;
  link?: string;
  className?: string;
  projectId?:string;
}

const Share: React.FC<ShareProps> = ({ label = 'Share', iconOnly, subject = 'Shared view', link, className,projectId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const [left, setLeft] = useState(0);
  const [top, setTop] = useState(0);
  const [width, setWidth] = useState(420);
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [results, setResults] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [recipients, setRecipients] = useState<Array<{ id?: string; email: string; name?: string }>>([]);

  const allUsersRef = useRef<Array<{ id: string; name: string; email: string }>>([]);
  const resultsRef = useRef<HTMLDivElement | null>(null);

  const isValidEmail = (v: string) => /.+@.+\..+/.test(v.trim());

  const open = () => {
    try {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (rect) {
        const desired = Math.min(440, Math.max(360, window.innerWidth - 24));
        setWidth(desired);
        let l = Math.max(12, Math.min(rect.left, window.innerWidth - desired - 12));
        setLeft(Math.round(l));
        setTop(Math.round(rect.bottom + 8));
      }
    } catch {}
    setIsOpen(true);
  };

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!isOpen) return;
      if (popRef.current && !popRef.current.contains(e.target as Node) && anchorRef.current && !anchorRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsOpen(false); };
    const onPos = () => {
      if (!isOpen) return;
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const desired = Math.min(440, Math.max(360, window.innerWidth - 24));
      setWidth(desired);
      let l = Math.max(12, Math.min(rect.left, window.innerWidth - desired - 12));
      setLeft(Math.round(l));
      setTop(Math.round(rect.bottom + 8));
    };
    document.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onPos);
    window.addEventListener('scroll', onPos, true);
    return () => {
      document.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onPos);
      window.removeEventListener('scroll', onPos, true);
    };
  }, [isOpen]);

  // Load default users and search
  // useEffect(() => {
  //   if (!isOpen) return;
  //   const q = to.trim();
  //   const fetchDefault = async () => {
  //     try {
  //       setLoading(true);
  //       let mapped;
  //       if(!projectId){
  //         const users = await userAPI.getUsers({ is_active: true, limit: 20 });
  //         mapped = (users || []).map((u: any) => ({ id: String(u.id), name: u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || u.email, email: u.email }));
  //       }
  //       else{
  //         const projectUsers= await projectMemberAPI.getProjectMembers(projectId,
  //           true,
  //          { skip: 0, limit: 100 })
  //           mapped = (projectUsers || []).map((u: any) => ({ id: String(u.id), name: u?.display_name || `${u?.first_name || ''} ${u?.last_name || ''}`.trim() || u?.username, email: u.user_email }));
  //       }
        
  //       setResults(mapped); setDropdownOpen(true);
  //     } catch { setResults([]); } finally { setLoading(false); }
  //   };
  //   const search = () => {
  //     const t = setTimeout(async () => {
  //       try {
  //         setLoading(true);
  //         const res = await userAPI.searchUsers(q);
  //         const mapped = (res || []).map((u: any) => ({ id: String(u.id), name: u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || u.email, email: u.email }));
  //         setResults(mapped); setDropdownOpen(true);
  //       } catch { setResults([]); } finally { setLoading(false); }
  //     }, 250);
  //     return () => clearTimeout(t);
  //   };
  //   if (q.length < 2) { fetchDefault(); return; }
  //   const cleanup = search(); return cleanup;
  // }, [to, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchUsers = async () => {
      try {
        setLoading(true);
        let mapped;
        if (!projectId) {
          const users = await userAPI.getUsers({ is_active: true, limit: 20 });
          mapped = (users || []).map((u: any) => ({ id: String(u.id), name: u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || u.email, email: u.email }));
        } else {
          const projectUsers = await projectMemberAPI.getProjectMembers(projectId, true, { skip: 0, limit: 100 });
          mapped = (projectUsers || []).map((u: any) => ({ id: String(u.id), name: u?.display_name || `${u?.first_name || ''} ${u?.last_name || ''}`.trim() || u?.username, email: u.user_email }));
        }
        allUsersRef.current = mapped;
        setResults(mapped);
        setDropdownOpen(true);
      } catch { allUsersRef.current = []; setResults([]); } finally { setLoading(false); }
    };
    fetchUsers();
  }, [isOpen]);

  // search
  useEffect(() => {
    const q = to.trim().toLowerCase();
    if (!q) { setResults(allUsersRef.current); return; }
    setResults(allUsersRef.current.filter(u =>
      u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    ));
    setDropdownOpen(true);
  }, [to]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => { if (resultsRef.current && !resultsRef.current.contains(e.target as Node)) setDropdownOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const addRecipient = (rec: { id?: string; email: string; name?: string }) => {
    if (recipients.find(r => r.email.toLowerCase() === rec.email.toLowerCase())) return;
    setRecipients(prev => [...prev, rec]); setTo(''); setDropdownOpen(false);
  };
  const removeRecipient = (email: string) => setRecipients(prev => prev.filter(r => r.email.toLowerCase() !== email.toLowerCase()));

  const submit = () => {
    const pending = to.trim();
    let addresses = recipients.map(r => r.email);
    if (isValidEmail(pending)) addresses = [...addresses, pending];
    if (addresses.length === 0) { toast.error('Please add at least one recipient'); return; }
    (async () => {
      try {
        await shareAPI.send({ recipients: addresses, subject, message, link: link || window.location.href });
        toast.success('Share email sent'); setIsOpen(false); setRecipients([]); setTo(''); setMessage('');
      } catch { toast.error('Failed to send share email'); }
    })();
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(link || window.location.href); toast.success('Link copied'); }
    catch { toast.error('Failed to copy link'); }
  };

  return (
    <div className="inline-block">
      <button ref={anchorRef} onClick={open} className={className || (iconOnly ? 'p-2 hover:bg-gray-100 rounded' : 'btn btn-secondary flex items-center gap-2')}>
        <Share2 className="w-4 h-4" />
        {!iconOnly && <span>{label}</span>}
      </button>
      {isOpen && (
        <div ref={popRef} className="fixed z-40 bg-white border border-gray-200 rounded-md shadow-xl" style={{ left, top, width, maxWidth: 'calc(100vw - 24px)' }}>
          <div className="p-4">
            <div className="text-base font-semibold text-gray-900 mb-2">Share</div>
            <div className="text-xs text-gray-500 mb-4">Required fields are marked with an asterisk<span className="text-red-500">*</span></div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Names, teams, or emails <span className="text-red-500">*</span></label>
            <div className="relative">
              <input className="w-full h-9 px-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="e.g. Maria, Team Orange, maria@company.com" value={to} onChange={(e)=>{ setTo(e.target.value); setDropdownOpen(true); }} onFocus={()=>setDropdownOpen(true)} />
              {dropdownOpen && (
                <div ref={resultsRef} className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-y-auto">
                  {loading && (<div className="px-3 py-2 text-xs text-gray-500">Loading...</div>)}
                  {results.map(u => (
                    <div key={u.id} className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer flex items-center gap-2" onClick={()=>addRecipient({ id: u.id, email: u.email, name: u.name })}>
                      <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[10px] text-gray-700">{(u.name || u.email).slice(0,2).toUpperCase()}</div>
                      <div className="min-w-0"><div className="truncate font-medium">{u.name || u.email}</div><div className="truncate text-xs text-gray-500">{u.email}</div></div>
                    </div>
                  ))}
                  {results.length === 0 && !loading && (<div className="px-3 py-2 text-xs text-gray-500">No matches</div>)}
                  {isValidEmail(to) && (<div className="px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 cursor-pointer" onClick={()=>addRecipient({ email: to.trim() })}>Add "{to.trim()}"</div>)}
                </div>
              )}
            </div>
            {recipients.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {recipients.map(r => (<span key={r.email} className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{r.name || r.email}<button className="hover:text-blue-900" onClick={()=>removeRecipient(r.email)}>×</button></span>))}
              </div>
            )}
            <label className="block text-sm font-medium text-gray-700 mt-4 mb-1">Message (optional)</label>
            <textarea className="w-full min-h-[90px] p-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="Anything they should know?" value={message} onChange={(e)=>setMessage(e.target.value)} />
            <div className="mt-3 flex items-center gap-3 text-sm text-gray-700">
              <button onClick={copyLink} className="text-blue-600 hover:text-blue-700">Copy link</button>
              <div className="flex-1" />
              <button onClick={submit} className="h-8 px-3 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700">Share</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Share;



